import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Badge, Box, IconButton, Tooltip, Typography } from "@mui/material"
import { IconSeeker, PopoverSeeker } from "@ui-seeker/core"
import { getSubmit, postSubmit } from "@ui-seeker/utils"

import { AppBulkEditAction } from "@components/@base/BulkEditDialog"
import { BRAND_GRADIENT } from "@components/@base/Dialog"
import AppEmptyLine from "@components/@base/EmptyLine"
import AppPillToggle from "@components/@base/PillToggle"
import AppTableSkeleton from "@components/@base/TableSkeleton"
import { headerIconButtonSx } from "@components/@base/ToolbarActions"
import { BACKEND_APIS } from "@constants"
import { dsColorNew } from "@palette"
import AppNewsDialog from "./Dialog"
import { formatDay, formatRelativeDate, groupByRecency } from "./@services/dates"
import { newsSeenTick } from "./signals"


const seen = id => postSubmit(`${BACKEND_APIS.ANNOUNCEMENTS}/${id}/seen`, {})

// Every announcement this user was entitled to see, read or not. `failed` only while nothing ever
// loaded: if the service is down on load the button is not drawn at all; once it has a list, a
// failed reload keeps that list.
function useNewsHistory() {
	const [entries, setEntries] = useState([])
	const [unread, setUnread] = useState(0)
	const [isLoading, setIsLoading] = useState(false)
	const [failed, setFailed] = useState(false)

	const reload = useCallback(() => {
		setIsLoading(true)
		return getSubmit(`${BACKEND_APIS.ANNOUNCEMENTS}/history`)
			.then(res => {
				setEntries(res?.data ?? [])
				setUnread(res?.unread ?? 0)
				setFailed(false)
			})
			.catch(() => setFailed(true))
			.finally(() => setIsLoading(false))
	}, [])

	useEffect(() => { reload() }, [reload])

	// the popup marking one as read is the only other thing that moves the badge
	useEffect(() => { if (newsSeenTick.value) reload() }, [newsSeenTick.value, reload])

	// Opening one counts as reading it
	const open = id => getSubmit(`${BACKEND_APIS.ANNOUNCEMENTS}/${id}`).then(detail => {
		seen(id).then(reload).catch(() => {})
		return detail
	})

	// Optimistic: the row clears now and the server is reconciled after
	const markSeen = id => {
		setEntries(previous => previous.map(entry => entry.id_announcement === id ? { ...entry, seen: true } : entry))
		setUnread(previous => Math.max(0, previous - 1))
		return seen(id).catch(() => {}).finally(reload)
	}

	// One `/seen` per unread entry: it is an idempotent upsert and nobody sits on fifty unread
	const markAllSeen = () => Promise.all(entries.filter(entry => !entry.seen).map(entry => seen(entry.id_announcement).catch(() => {})))
		.then(reload)

	return { entries, unread, isLoading, failed: failed && !entries.length, reload, open, markSeen, markAllSeen }
}

function NewsRow({ entry, onOpen, onMarkSeen }) {
	return <Box
		onClick={() => onOpen(entry)}
		sx={{
			display: 'flex', alignItems: 'flex-start', gap: 1.25, px: 2, py: 1.25, cursor: 'pointer',
			borderBottom: `1px solid ${dsColorNew.grayBorder}`,
			'&:hover': { backgroundColor: dsColorNew.hoverBgLight },
			// the row action only once the pointer is on the row
			'&:hover .row-action': { opacity: 1 },
			...(!entry.seen && { boxShadow: `inset 3px 0 0 ${dsColorNew.appBlue}` }),
		}}
	>
		<Box sx={{
			width: 30, height: 30, flexShrink: 0, mt: '2px', borderRadius: '8px',
			display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
			...(entry.seen
				? { backgroundColor: dsColorNew.filterInputBg, border: `1px solid ${dsColorNew.grayBorder}`, color: dsColorNew.textSubtle }
				: { background: BRAND_GRADIENT, color: dsColorNew.white }),
		}}>
			<IconSeeker icon="bullhorn" sx={{ fontSize: 12 }}/>
		</Box>
		<Box sx={{ flex: 1, minWidth: 0 }}>
			<Typography sx={{ fontSize: 13.5, lineHeight: 1.35, fontWeight: entry.seen ? 400 : 600, color: dsColorNew.textTitle }}>
				{entry.title}
			</Typography>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
				<Typography title={formatDay(entry.start_at)} sx={{ fontSize: 11.5, color: dsColorNew.textSubtle }}>
					{formatRelativeDate(entry.start_at)}
				</Typography>
				{!entry.seen && <Typography
					className="row-action"
					onClick={event => {
						// the row would open it otherwise: the opposite of clearing it unread
						event.stopPropagation()
						onMarkSeen(entry.id_announcement)
					}}
					sx={{
						fontSize: 11.5, fontWeight: 600, cursor: 'pointer', color: dsColorNew.appBlue,
						opacity: 0, transition: 'opacity .15s ease', '&:hover': { textDecoration: 'underline' },
					}}
				>
					Mark as read
				</Typography>}
			</Box>
		</Box>
	</Box>
}

/**
 * "News" in the header: the announcements already published, read or not. The megaphone and not a
 * bell, which in this app already means alerts; the count in the app's blue, not the red of errors.
 * Unlike the popup this surface is consulted, never imposed.
 */
export default function AppNewsButton() {
	const { entries, unread, isLoading, failed, reload, open, markSeen, markAllSeen } = useNewsHistory()
	const [anchor, setAnchor] = useState(null)
	const [reading, setReading] = useState(null)
	const [filter, setFilter] = useState('all')

	const visible = useMemo(() => filter === 'unread' ? entries.filter(entry => !entry.seen) : entries, [entries, filter])
	const groups = useMemo(() => groupByRecency(visible), [visible])

	if (failed) return null

	const openEntry = entry => {
		setAnchor(null)
		open(entry.id_announcement).then(setReading).catch(() => {})
	}

	return <>
		<Tooltip title="News" arrow>
			<IconButton
				aria-label={unread ? `News, ${unread} unread` : 'News'}
				// reloaded on open: anything published while the tab stayed open would be missing
				onClick={event => { setAnchor(event.currentTarget); reload() }}
				sx={headerIconButtonSx}
			>
				<Badge
					badgeContent={unread}
					invisible={!unread}
					sx={{ '& .MuiBadge-badge': {
						top: -6, right: -8, height: 16, minWidth: 16, px: 0.5, fontSize: 10, fontWeight: 700,
						backgroundColor: dsColorNew.appBlue, color: `${dsColorNew.white} !important`,
					} }}
				>
					<IconSeeker icon="bullhorn" sx={{ fontSize: 16 }}/>
				</Badge>
			</IconButton>
		</Tooltip>

		{/* PopoverSeeker, as the user menu next to it */}
		<PopoverSeeker
			anchorEl={anchor}
			onClose={() => setAnchor(null)}
			slotProps={{ paper: { sx: { width: 380, p: 0, borderRadius: '14px' } } }}
		>
			{/* the scroll lives inside: the paper keeps `overflow: inherit` for the pointer arrow */}
			<Box sx={{ maxHeight: 480, overflowY: 'auto', borderRadius: '14px' }}>
				{/* header and filter stay on screen while a long history scrolls */}
				<Box sx={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: dsColorNew.white, borderBottom: `1px solid ${dsColorNew.grayBorder}` }}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2, pt: 1.75, pb: 1.25 }}>
						<Box sx={{
							width: 28, height: 28, borderRadius: '8px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
							background: BRAND_GRADIENT, color: dsColorNew.white,
						}}>
							<IconSeeker icon="bullhorn" sx={{ fontSize: 12 }}/>
						</Box>
						<Typography sx={{ fontSize: 15, fontWeight: 600, color: dsColorNew.textTitle }}>News</Typography>
						{unread > 0 && <Typography
							onClick={markAllSeen}
							sx={{ ml: 'auto', cursor: 'pointer', fontSize: 12.5, fontWeight: 500, color: dsColorNew.appBlue, '&:hover': { textDecoration: 'underline' } }}
						>
							Mark all as read
						</Typography>}
					</Box>
					<Box sx={{ px: 2, pb: 1.25 }}>
						<AppPillToggle
							size="small"
							value={filter}
							onChange={setFilter}
							options={[
								{ value: 'all', label: `All${entries.length ? ` ${entries.length}` : ''}` },
								{ value: 'unread', label: `Unread${unread ? ` ${unread}` : ''}` },
							]}
						/>
					</Box>
				</Box>

				{isLoading && !entries.length && <Box sx={{ p: 2 }}><AppTableSkeleton rows={3} columns={1}/></Box>}

				{!isLoading && !visible.length && <Box sx={{ px: 2, py: 1 }}>
					<AppEmptyLine>{filter === 'unread' ? "You're all caught up" : 'No news yet'}</AppEmptyLine>
				</Box>}

				{groups.map(group => <Box key={group.name}>
					<Typography sx={{
						px: 2, pt: 1.5, pb: 0.5, fontSize: 11, fontWeight: 600, color: dsColorNew.textSubtle,
						textTransform: 'uppercase', letterSpacing: 0.6,
					}}>
						{group.name}
					</Typography>
					{group.items.map(entry => <NewsRow key={entry.id_announcement} entry={entry} onOpen={openEntry} onMarkSeen={markSeen}/>)}
				</Box>)}
			</Box>
		</PopoverSeeker>

		{/* the same dialog as the popup: one announcement must not look like two things */}
		<AppNewsDialog
			open={!!reading}
			onClose={() => setReading(null)}
			title={reading?.title}
			date={reading?.start_at}
			id={reading?.id_announcement}
			body={reading?.body}
			footer={<AppBulkEditAction onClick={() => setReading(null)}>Done</AppBulkEditAction>}
		/>
	</>
}
